import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addModuleDrive, removeModuleDrive } from "../../../store/features/moduleSlice";
import { DriveType } from "../../../types/drive";
import { formatNumber, formatCurrency } from "../../../utils/formatting";
import { DRIVE_POTENTIAL } from "../../../constants/drives";

/**
 * Module Drives Step Component
 * Handles drive system selection and configuration for modules
 */
export const ModuleDrivesStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const drives = useAppSelector((state) => state.module.moduleConfig?.systems.drives || []);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  // Drive percentage lookup tables based on Traveller rules
  const MANEUVER_DRIVE_PERCENTAGES: Record<number, number> = {
    0: 0.5, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8,
    9: 9, 10: 10, 11: 11, 12: 12, 13: 13, 14: 14, 15: 15, 16: 16
  };

  const REACTION_DRIVE_PERCENTAGES: Record<number, number> = {
    0: 1, 1: 2, 2: 4, 3: 6, 4: 8, 5: 10, 6: 12, 7: 14, 8: 16,
    9: 18, 10: 20, 11: 22, 12: 24, 13: 26, 14: 28, 15: 30, 16: 32
  };

  const JUMP_DRIVE_PERCENTAGES: Record<number, number> = {
    1: 2.5, 2: 5, 3: 7.5, 4: 10, 5: 12.5, 6: 15, 7: 17.5, 8: 20, 9: 22.5
  };

  /**
   * Helper function to get drive percentage based on type and rating
   */
  const getDrivePercentage = (type: DriveType, rating: number): number => {
    if (type === DriveType.MANEUVER) {
      return MANEUVER_DRIVE_PERCENTAGES[rating] || 0;
    }
    if (type === DriveType.REACTION) {
      return REACTION_DRIVE_PERCENTAGES[rating] || 0;
    }
    if (type === DriveType.JUMP) {
      return JUMP_DRIVE_PERCENTAGES[rating] || 0;
    }
    return 0;
  };

  /**
   * Calculates drive tonnage based on type, rating, and module size
   */
  const calculateDriveTonnage = (type: DriveType, rating: number, moduleSize: number): number => {
    if (rating === -1) return 0; // No drive
    const percentage = getDrivePercentage(type, rating);
    return (percentage / 100) * moduleSize;
  };

  /**
   * Calculates drive power consumption based on type, rating, and module size
   */
  const calculateDrivePower = (type: DriveType, rating: number, moduleSize: number): number => {
    if (rating === -1) return 0; // No drive
    const tonnage = calculateDriveTonnage(type, rating, moduleSize);
    if (type === DriveType.MANEUVER || type === DriveType.JUMP) {
      return tonnage * 0.1; // 0.1 Power per ton for maneuver and jump drives
    }
    return 0; // Reaction drives don't consume power
  };

  /**
   * Calculates drive cost based on type, rating, and module size
   */
  const calculateDriveCost = (type: DriveType, rating: number, moduleSize: number): number => {
    if (rating === -1) return 0; // No drive
    const tonnage = calculateDriveTonnage(type, rating, moduleSize);
    if (type === DriveType.MANEUVER) {
      return tonnage * 500000; // Cr500,000 per ton for maneuver drives
    }
    if (type === DriveType.JUMP) {
      return tonnage * 1000000; // Cr1,000,000 per ton for jump drives
    }
    if (type === DriveType.REACTION) {
      return tonnage * 300000; // Cr300,000 per ton for reaction drives
    }
    return 0;
  };

  /**
   * Get available drive options for a given type
   */
  const getDriveOptions = (type: DriveType) => {
    if (type === DriveType.MANEUVER) {
      return DRIVE_POTENTIAL.MANEUVER.filter(drive => drive.techLevel <= moduleConfig.techLevel);
    }
    if (type === DriveType.REACTION) {
      return DRIVE_POTENTIAL.REACTION.filter(drive => drive.techLevel <= moduleConfig.techLevel);
    }
    if (type === DriveType.JUMP) {
      return DRIVE_POTENTIAL.JUMP.filter(drive => drive.techLevel <= moduleConfig.techLevel);
    }
    return [];
  };

  /**
   * Handle adding a drive
   */
  const handleAddDrive = (type: DriveType, rating: number) => {
    const tonnage = calculateDriveTonnage(type, rating, moduleConfig.size);
    const power = calculateDrivePower(type, rating, moduleConfig.size);
    const cost = calculateDriveCost(type, rating, moduleConfig.size);

    const drive = {
      type,
      rating,
      tonnage,
      power,
      cost,
      techLevel: getDriveOptions(type).find(d => d.rating === rating)?.techLevel || 9,
      name: `${type === DriveType.MANEUVER ? 'M' : type === DriveType.JUMP ? 'J' : 'R'}-${rating} Drive`,
      id: `${type}_${rating}_${Date.now()}`,
    };

    dispatch(addModuleDrive(drive));
  };

  /**
   * Handle removing a drive
   */
  const handleRemoveDrive = (index: number) => {
    dispatch(removeModuleDrive(index));
  };

  /**
   * Get current drive of a specific type
   */
  const getCurrentDrive = (type: DriveType) => {
    return drives.find(d => d.type === type);
  };

  // Calculate totals
  const totalDriveTonnage = drives.reduce((sum, drive) => sum + (drive.tonnage || 0), 0);
  const totalDrivePower = drives.reduce((sum, drive) => sum + (drive.power || 0), 0);
  const totalDriveCost = drives.reduce((sum, drive) => sum + (drive.cost || 0), 0);
  const remainingTons = moduleConfig.size - totalDriveTonnage;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Module Drive Systems Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Install drive systems for your module
        </p>
      </div>

      {/* Module Information */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Module Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Module Size:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.size)} tons</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Tech Level:</span>
            <div className="text-blue-900 dark:text-blue-100">TL {moduleConfig.techLevel}</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Available Power:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {moduleConfig.calculations.totalPower} Power
            </div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Remaining Tons:</span>
            <div className={`font-semibold ${remainingTons < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatNumber(remainingTons)} tons
            </div>
          </div>
        </div>
      </div>

      {/* Drive Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Drive Configuration
        </h3>
        
        <div className="space-y-6">
          {/* Maneuver Drives */}
          <div>
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Maneuver Drives</h4>
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Maneuver Drive Rating</label>
              <select
                value={getCurrentDrive(DriveType.MANEUVER)?.rating || "none"}
                onChange={(event) => {
                  const rating = event.target.value === "none" ? null : parseInt(event.target.value);
                  if (rating !== null) {
                    handleAddDrive(DriveType.MANEUVER, rating);
                  } else {
                    // Remove existing maneuver drive
                    const currentIndex = drives.findIndex(d => d.type === DriveType.MANEUVER);
                    if (currentIndex !== -1) {
                      handleRemoveDrive(currentIndex);
                    }
                  }
                }}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="none">No Maneuver Drive</option>
                {getDriveOptions(DriveType.MANEUVER).map((drive) => {
                  const tonnage = calculateDriveTonnage(DriveType.MANEUVER, drive.rating, moduleConfig.size);
                  const power = calculateDrivePower(DriveType.MANEUVER, drive.rating, moduleConfig.size);
                  const cost = calculateDriveCost(DriveType.MANEUVER, drive.rating, moduleConfig.size);
                  const wouldExceedTonnage = (totalDriveTonnage + tonnage) > moduleConfig.size;
                  const isDisabled = wouldExceedTonnage;

                  return (
                    <option
                      key={drive.rating}
                      value={drive.rating}
                      disabled={isDisabled}
                      title={
                        isDisabled
                          ? `Would exceed module capacity by ${formatNumber((totalDriveTonnage + tonnage) - moduleConfig.size)} tons`
                          : `${formatNumber(tonnage)} tons • ${formatNumber(power)} Power • ${formatCurrency(cost)}`
                      }
                    >
                      M-{drive.rating} Drive ({formatNumber(tonnage)} tons, {formatNumber(power)} Power, {formatCurrency(cost)})
                      {isDisabled ? " - Would exceed capacity" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Jump Drives */}
          <div>
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Jump Drives</h4>
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Jump Drive Rating</label>
              <select
                value={getCurrentDrive(DriveType.JUMP)?.rating || "none"}
                onChange={(event) => {
                  const rating = event.target.value === "none" ? null : parseInt(event.target.value);
                  if (rating !== null) {
                    handleAddDrive(DriveType.JUMP, rating);
                  } else {
                    // Remove existing jump drive
                    const currentIndex = drives.findIndex(d => d.type === DriveType.JUMP);
                    if (currentIndex !== -1) {
                      handleRemoveDrive(currentIndex);
                    }
                  }
                }}
                className="rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="none">No Jump Drive</option>
                {getDriveOptions(DriveType.JUMP).map((drive) => {
                  const tonnage = calculateDriveTonnage(DriveType.JUMP, drive.rating, moduleConfig.size);
                  const power = calculateDrivePower(DriveType.JUMP, drive.rating, moduleConfig.size);
                  const cost = calculateDriveCost(DriveType.JUMP, drive.rating, moduleConfig.size);
                  const wouldExceedTonnage = (totalDriveTonnage + tonnage) > moduleConfig.size;
                  const isDisabled = wouldExceedTonnage;

                  return (
                    <option
                      key={drive.rating}
                      value={drive.rating}
                      disabled={isDisabled}
                      title={
                        isDisabled
                          ? `Would exceed module capacity by ${formatNumber((totalDriveTonnage + tonnage) - moduleConfig.size)} tons`
                          : `${formatNumber(tonnage)} tons • ${formatNumber(power)} Power • ${formatCurrency(cost)}`
                      }
                    >
                      Jump-{drive.rating} Drive ({formatNumber(tonnage)} tons, {formatNumber(power)} Power, {formatCurrency(cost)})
                      {isDisabled ? " - Would exceed capacity" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Reaction Drives */}
          <div>
            <h4 className="text-md font-medium text-gray-800 mb-3">Reaction Drives</h4>
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium text-gray-700">Reaction Drive Rating</label>
              <select
                value={getCurrentDrive(DriveType.REACTION)?.rating || "none"}
                onChange={(event) => {
                  const rating = event.target.value === "none" ? null : parseInt(event.target.value);
                  if (rating !== null) {
                    handleAddDrive(DriveType.REACTION, rating);
                  } else {
                    // Remove existing reaction drive
                    const currentIndex = drives.findIndex(d => d.type === DriveType.REACTION);
                    if (currentIndex !== -1) {
                      handleRemoveDrive(currentIndex);
                    }
                  }
                }}
                className="rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="none">No Reaction Drive</option>
                {getDriveOptions(DriveType.REACTION).map((drive) => {
                  const tonnage = calculateDriveTonnage(DriveType.REACTION, drive.rating, moduleConfig.size);
                  const power = calculateDrivePower(DriveType.REACTION, drive.rating, moduleConfig.size);
                  const cost = calculateDriveCost(DriveType.REACTION, drive.rating, moduleConfig.size);
                  const wouldExceedTonnage = (totalDriveTonnage + tonnage) > moduleConfig.size;
                  const isDisabled = wouldExceedTonnage;

                  return (
                    <option
                      key={drive.rating}
                      value={drive.rating}
                      disabled={isDisabled}
                      title={
                        isDisabled
                          ? `Would exceed module capacity by ${formatNumber((totalDriveTonnage + tonnage) - moduleConfig.size)} tons`
                          : `${formatNumber(tonnage)} tons • ${formatNumber(power)} Power • ${formatCurrency(cost)}`
                      }
                    >
                      R-{drive.rating} Drive ({formatNumber(tonnage)} tons, {formatNumber(power)} Power, {formatCurrency(cost)})
                      {isDisabled ? " - Would exceed capacity" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* Installed Drives */}
        {drives.length > 0 && (
          <div className="mt-6">
            <h4 className="text-md font-medium text-gray-800 mb-3">Installed Drives</h4>
            <div className="space-y-2">
              {drives.map((drive, index) => (
                <div key={drive.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div>
                    <span className="font-medium">{drive.name}</span>
                    <span className="text-sm text-gray-600 ml-2">
                      {formatNumber(drive.tonnage)} tons • {formatNumber(drive.power)} Power • {formatCurrency(drive.cost)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveDrive(index)}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer with Drive Stats */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
        <div className="container mx-auto grid grid-cols-4 gap-4 items-center text-center">
          <div>
            <span className="text-gray-400 block text-sm">Power</span>
            <span className="text-xl font-bold text-yellow-400">
              {formatNumber(totalDrivePower)} Power
            </span>
          </div>
          <div>
            <span className="text-gray-400 block text-sm">Weight</span>
            <span className="text-xl font-bold text-blue-400">
              {formatNumber(totalDriveTonnage)} tons
            </span>
          </div>
          <div>
            <span className="text-gray-400 block text-sm">Cost</span>
            <span className="text-xl font-bold text-green-400">
              {formatCurrency(totalDriveCost)}
            </span>
          </div>
          <div>
            <span className="text-gray-400 block text-sm">Drives</span>
            <span className="text-xl font-bold text-purple-400">
              {drives.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
